import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getMonthlyRewardBoard, type MonthlyRewardResponse, type MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { isMonthlyRewardConfigUpdate, MONTHLY_REWARD_UNAVAILABLE_TEXT } from '../../utils/monthlyRewardTabs'

interface RewardLeaderboardPageProps {
  tab: MonthlyRewardTabKey
}

const RewardLeaderboardPage: React.FC<RewardLeaderboardPageProps> = ({ tab }) => {
  const [data, setData] = useState<MonthlyRewardResponse['data'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await getMonthlyRewardBoard(tab)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
      }
      setData(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void loadBoard()
  }, [loadBoard])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        type?: string
        promotionType?: string
        contentType?: string
        content?: {
          type?: string
          promotionType?: string
        }
      } | undefined

      if (isMonthlyRewardConfigUpdate(detail)) {
        void loadBoard()
      }
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadBoard])

  useEffect(() => {
    if (!data?.summary?.fakeCount || !data.fakeDataRotatesAt) return
    const rotateAt = new Date(data.fakeDataRotatesAt).getTime()
    if (!Number.isFinite(rotateAt)) return
    const delay = Math.max(1000, rotateAt - Date.now())
    const timer = window.setTimeout(() => {
      void loadBoard()
    }, delay)
    return () => window.clearTimeout(timer)
  }, [data?.fakeDataRotatesAt, data?.summary?.fakeCount, loadBoard])

  const rankBadge = (rank: number): { label: string; className: string } => {
    if (rank === 1) return { label: '🥇', className: 'htd-reward-rank htd-reward-rank--medal' }
    if (rank === 2) return { label: '🥈', className: 'htd-reward-rank htd-reward-rank--medal' }
    if (rank === 3) return { label: '🥉', className: 'htd-reward-rank htd-reward-rank--medal' }
    return { label: String(rank), className: 'htd-reward-rank' }
  }

  const emptyText = data?.emptyText || 'ยังไม่มีข้อมูลรางวัลประจำเดือน'
  const isPreparingState = emptyText === MONTHLY_REWARD_UNAVAILABLE_TEXT
  const podiumItems = useMemo(() => (data?.items || []).filter((row) => row.rank <= 3).slice(0, 3), [data?.items])
  const restItems = useMemo(() => (data?.items || []).filter((row) => row.rank > 3), [data?.items])

  return (
    <div className="htd-reward-board">
      <div className="htd-reward-board__header">
        <div>
          <p className="htd-reward-board__kicker">{data?.monthLabel || 'เดือนปัจจุบัน'}</p>
          <h2 className="htd-reward-board__title">{data?.title || 'รางวัลประจำเดือน'}</h2>
          {data?.subtitle ? <p className="htd-reward-board__subtitle">{data.subtitle}</p> : null}
        </div>
      </div>

      {loading ? (
        <div className="htd-reward-board__state">กำลังโหลดข้อมูล...</div>
      ) : error ? (
        <div className="htd-reward-board__state htd-reward-board__state--error">{error}</div>
      ) : data?.items?.length ? (
        <>
          {podiumItems.length >= 3 ? (
            <div className="htd-reward-podium" aria-label="Top 3">
              {podiumItems.map((row) => {
                const badge = rankBadge(row.rank)
                return (
                  <div
                    key={`${tab}-podium-${row.rank}`}
                    className={`htd-reward-podium__card htd-reward-podium__card--${row.rank}`}
                  >
                    <div className="htd-reward-podium__rank-wrap">
                      <span className="htd-reward-podium__rank" aria-label={`อันดับ ${row.rank}`}>
                        {row.rank}
                      </span>
                      <span className="htd-reward-podium__medal" aria-hidden>
                        {badge.label}
                      </span>
                    </div>
                    <span className="htd-reward-podium__place">อันดับที่ {row.rank}</span>
                    <span className="htd-reward-podium__user">{row.user}</span>
                    <span className="htd-reward-podium__metric">{row.metricValue}</span>
                    <span className="htd-reward-podium__prize">{row.reward}</span>
                    <span className="htd-reward-podium__base" aria-hidden />
                  </div>
                )
              })}
            </div>
          ) : null}

          <div className="htd-reward-table" aria-label="ตารางอันดับ">
            <div className="htd-reward-table__head">
              <span>อันดับ</span>
              <span>ยูสเซอร์</span>
              <span style={{ textAlign: 'right' }}>{data.metricLabel}</span>
              <span style={{ textAlign: 'right' }}>{data.rewardLabel}</span>
            </div>
            {(podiumItems.length >= 3 ? restItems : data.items).map((row) => {
              const badge = rankBadge(row.rank)
              return (
                <div
                  key={`${tab}-desktop-${row.rank}`}
                  className={`htd-reward-table__row${row.rank <= 3 ? ' htd-reward-table__row--top' : ''}`}
                >
                  <span className="htd-reward-table__rank">
                    <span className={badge.className}>{badge.label}</span>
                  </span>
                  <span className="htd-reward-table__user">{row.user}</span>
                  <span className="htd-reward-table__metric">{row.metricValue}</span>
                  <span className="htd-reward-table__prize">{row.reward}</span>
                </div>
              )
            })}
          </div>

          <div className="htd-reward-cards" aria-label="การ์ดอันดับ">
            {data.items.map((row) => {
              const badge = rankBadge(row.rank)
              const isTop = row.rank <= 3
              return (
                <div
                  key={`${tab}-mobile-${row.rank}`}
                  className={`htd-reward-card${isTop ? ` htd-reward-card--top htd-reward-card--rank-${row.rank}` : ''}`}
                >
                  <div className="htd-reward-card__top">
                    <span
                      className={`htd-reward-card__rank${isTop ? ` htd-reward-card__rank--${row.rank}` : ''}`}
                      aria-label={`อันดับ ${row.rank}`}
                    >
                      {row.rank}
                    </span>
                    {isTop ? (
                      <span className="htd-reward-card__medal" aria-hidden>
                        {badge.label}
                      </span>
                    ) : null}
                    <div className="htd-reward-card__identity">
                      {isTop ? (
                        <span className="htd-reward-card__place">อันดับที่ {row.rank}</span>
                      ) : null}
                      <span className="htd-reward-card__user">{row.user}</span>
                    </div>
                  </div>
                  <div className="htd-reward-card__details">
                    <div className="htd-reward-card__detail">
                      <span className="htd-reward-card__label">{data.metricLabel}</span>
                      <span className="htd-reward-card__value">{row.metricValue}</span>
                    </div>
                    <div className="htd-reward-card__detail htd-reward-card__detail--prize">
                      <span className="htd-reward-card__label">{data.rewardLabel}</span>
                      <span className="htd-reward-card__value">{row.reward}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className="htd-reward-board__state">
          {isPreparingState ? <div className="htd-reward-board__empty-icon" aria-hidden>✨</div> : null}
          <p className="mb-0">{emptyText}</p>
        </div>
      )}
    </div>
  )
}

export default RewardLeaderboardPage
